import { CSSProperties } from 'react';
import { Box } from '@mui/material';
import { EEOccupationalLevel, SeverityLevel, DemographicGroup } from '../../../../Component/Model/Enum/EmploymentEquity';
import { DeviationsDto } from '../../../../Component/Model/Dto/EmploymentEquity/Deviations';
import colors from '../../../../assets/theme/base/colors';
import { HeatMapCell } from './HeatMapCell';
import { DesignatedDisabledBar } from './DesignatedDisabledBar';
import { HeatMapLegend } from './HeatMapLegend';
import {
    HeatMapCellData,
    occupationalLevels,
    demographicGroups,
    occupationalLevelDisplayNames,
} from './deviationsSummaryHelper';


interface HeatMapGridProps {
    data: HeatMapCellData[];
    selectedSeverities?: Set<SeverityLevel>;
    selectedLevels?: Set<EEOccupationalLevel>;
    selectedDemographicGroups?: Set<DemographicGroup>;
    visibleDemographics?: DemographicGroup[];
    designatedDisabled?: DeviationsDto.HeatmapDesignatedDisabledDto | null;
    hidePercentages?: boolean;
    hideTarget?: boolean;
    cellWidth?: number;
    showHeadcounts?: boolean;
    onCellClick?: (cell: HeatMapCellData) => void;
}

const defaultCellWidth = 130;
const rowHeaderWidth = 130;

const headerStyle: CSSProperties = {
    padding: '4px',
    textAlign: 'center',
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: colors.dark.main,
    borderBottom: `1px solid ${colors.ui.border}`,
    lineHeight: 1.25,
    wordBreak: 'break-word',
};


export function HeatMapGrid({
    data,
    selectedSeverities,
    selectedLevels,
    selectedDemographicGroups,
    visibleDemographics,
    designatedDisabled,
    hidePercentages = false,
    hideTarget = false,
    cellWidth = defaultCellWidth,
    showHeadcounts = true,
    onCellClick,
}: HeatMapGridProps) {
    const activeDemographics = visibleDemographics ?? demographicGroups;
    const hasAnyFilter =
        (selectedSeverities?.size ?? 0) > 0 ||
        (selectedLevels?.size ?? 0) > 0 ||
        (selectedDemographicGroups?.size ?? 0) > 0;

    const cellMap = new Map<string, HeatMapCellData>();
    data.forEach(cell => {
        cellMap.set(`${cell.occupationalLevel}-${cell.demographicGroup}`, cell);
    });

    const tableWidth = rowHeaderWidth + activeDemographics.length * (cellWidth + 8);

    return (
        <Box>
            <Box sx={{ overflowX: 'auto', scrollbarGutter: 'stable' }}>
                <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: tableWidth }}>
                    <thead>
                        <tr>
                            <th style={{ ...headerStyle, width: rowHeaderWidth, textAlign: 'left' }} />
                            {activeDemographics.map(group => {
                                const dimHeader = (selectedDemographicGroups?.size ?? 0) > 0 && !selectedDemographicGroups!.has(group);
                                return (
                                    <th
                                        key={group}
                                        style={{ ...headerStyle, width: cellWidth + 8, opacity: dimHeader ? 0.2 : 1 }}
                                    >
                                        {group}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {occupationalLevels.map(level => {
                            const dimRow = (selectedLevels?.size ?? 0) > 0 && !selectedLevels!.has(level);
                            return (
                                <tr key={level}>
                                    <td
                                        style={{
                                            padding: '4px',
                                            fontSize: '0.8125rem',
                                            fontWeight: 500,
                                            color: dimRow ? colors.ui.dimmedText : colors.dark.main,
                                            borderBottom: `1px solid ${colors.ui.border}`,
                                            verticalAlign: 'middle',
                                            width: rowHeaderWidth,
                                            lineHeight: 1.25,
                                        }}
                                    >
                                        {occupationalLevelDisplayNames[level]}
                                    </td>
                                    {activeDemographics.map(group => {
                                        const key = `${level}-${group}`;
                                        const cell = cellMap.get(key);
                                        const isDimmed = hasAnyFilter && (
                                            (selectedSeverities?.size ? !selectedSeverities.has(cell?.severity ?? SeverityLevel.OnTrack) : false) ||
                                            dimRow ||
                                            ((selectedDemographicGroups?.size ?? 0) > 0 && !selectedDemographicGroups!.has(group))
                                        );
                                        return (
                                            <td
                                                key={key}
                                                style={{
                                                    padding: '4px',
                                                    borderBottom: `1px solid ${colors.ui.border}`,
                                                    textAlign: 'center',
                                                    width: cellWidth + 8,
                                                }}
                                            >
                                                {cell ? (
                                                    <HeatMapCell
                                                        gap={cell.gap}
                                                        gapCount={cell.gapCount}
                                                        gapPctRelative={cell.gapPctRelative}
                                                        target={cell.target}
                                                        targetCount={cell.targetCount}
                                                        current={cell.current}
                                                        currentCount={cell.currentCount}
                                                        severity={cell.severity}
                                                        showHeadcounts={showHeadcounts}
                                                        dimmed={isDimmed}
                                                        hidePercentages={hidePercentages}
                                                        hideTarget={hideTarget}
                                                        cellWidth={cellWidth}
                                                        priorityRank={cell.priorityRank}
                                                        onClick={onCellClick ? () => onCellClick(cell) : undefined}
                                                    />
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            width: cellWidth,
                                                            height: 80,
                                                            border: `2px dashed ${colors.ui.naBorder}`,
                                                            borderRadius: '4px',
                                                            backgroundColor: colors.ui.naBackground,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <Box component="span" sx={{ fontSize: '0.75rem', color: colors.ui.mutedText }}>N/A</Box>
                                                    </Box>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </Box>

            {designatedDisabled && <DesignatedDisabledBar rowHeaderWidth={rowHeaderWidth} data={designatedDisabled} />}
            <HeatMapLegend cellWidth={cellWidth} />
        </Box>
    );
}
